import React from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

/**
 * Top-level error boundary so one component failure doesn't blank the site.
 * Logs to console + lets the user reload or go home.
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Surface the error so it's visible in any monitoring/log capture
    // eslint-disable-next-line no-console
    console.error("ErrorBoundary caught:", error, info);
  }

  reset = () => this.setState({ hasError: false, error: null });

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-4" data-testid="error-boundary">
        <div className="max-w-lg text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-amber-500/10 border border-amber-500/30 mb-6">
            <AlertTriangle className="w-8 h-8 text-amber-500" />
          </div>
          <h1 className="font-serif text-3xl font-bold text-white mb-3">
            Something went off the rails.
          </h1>
          <p className="text-white/60 mb-6">
            A piece of this page crashed unexpectedly. The rest of the site is fine — try reloading,
            or head back home and try a different route.
          </p>
          <pre className="text-left text-xs bg-white/5 border border-white/10 rounded-lg p-3 text-white/40 max-h-32 overflow-auto mb-6">
            {String(this.state.error?.message || this.state.error)}
          </pre>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <button
              onClick={() => window.location.reload()}
              className="bg-amber-500 hover:bg-amber-600 text-black font-semibold rounded-lg px-5 py-2.5 inline-flex items-center gap-2 transition-colors"
              data-testid="error-reload"
            >
              <RefreshCw className="w-4 h-4" /> Reload page
            </button>
            <a
              href="/"
              className="bg-white/5 hover:bg-white/10 text-white border border-white/15 rounded-lg px-5 py-2.5 transition-colors"
              data-testid="error-home"
            >
              Back to map
            </a>
          </div>
          <p className="text-xs text-white/30 mt-8">
            If this keeps happening, please email{" "}
            <a href="mailto:takedown@railstream.net" className="text-amber-500 hover:underline">
              takedown@railstream.net
            </a>
            .
          </p>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
